/**
 * StandPlan-Komponente
 * -------------------
 * Zeigt einen interaktiven Standplan mit Herstellern.
 * 
 * Features:
 * - SVG-Visualisierung aller Stände
 * - Klick- und Hover-Effekte
 * - Hervorhebung basierend auf Suche (highlightStands) und Kategorien (selectedCategories)
 * - Anzeige von Kategorie- und Such-Treffern oben
 * - Herstellerliste mit Details (Name, Bild, Kategorie, Produkte, Beschreibung)
 *
 * Props:
 * - highlightStands: Array von Ständen, die über die Suche markiert werden
 * - selectedCategories: Array von Kategorien, nach denen gefiltert wird
 * - printable: Boolean, ob der Plan für den Druck vorbereitet werden soll
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import StandModal from "./StandModal";
import { useLanguage } from "./LanguageContext";
import data from "../data/data.json";

// SVG-Standplan Dimensionen
const VIEWBOX_WIDTH = 500;
const VIEWBOX_HEIGHT = 130;

export default function StandPlan({
  highlightStands = [],
  selectedCategories = [],
  printable = false,
  pdfMode = false,
}) {
  const { lang } = useLanguage(); // Aktuelle Sprache
  const [activeStand, setActiveStand] = useState(null); // Aktiver Stand für Modal
  const [manufacturersForMatches, setManufacturersForMatches] = useState([]); // Hersteller für Treffer
  const [hoveredStandId, setHoveredStandId] = useState(null); // Hover-Zustand
  const svgRef = useRef(null); // Referenz auf SVG-Element

  /**
   * Übersetzungsfunktion
   * @param field - Text oder Objekt mit Sprachvarianten
   * @param fallback - Fallback-Sprache (default "de")
   */
  const t = useCallback(
    (field, fallback = "de") => {
      if (!field) return "";
      if (typeof field === "string") return field;
      return field[lang] ?? field[fallback] ?? "";
    },
    [lang]
  );

  /**
   * Prüft, ob eine ausgewählte Kategorie in einem
   * kommagetrennten Kategorie-String vorkommt
   * - berücksichtigt die aktuelle Sprache (de oder en)
   * - z.B. "Gemüse & Obst, Getränke" oder "Vegetables & Fruit, Beverages"
   */
  const standHasCategory = useCallback(
  (standCategory, selectedCategory) => {
    if (!standCategory) return false;
    const translated = t(standCategory);
    const categories = translated
      .split(",")
      .map((c) => c.toLowerCase().trim());
    return categories.includes(selectedCategory.toLowerCase().trim());
  },
  [t]
);

  /* ===================== RESIZE OBSERVER ===================== */
  useEffect(() => {
    if (printable) return; // Deaktiviert im Druckmodus
    const observer = new ResizeObserver(() => {});
    if (svgRef.current) observer.observe(svgRef.current);
    return () => observer.disconnect();
  }, [printable]);

  /* ===================== HERSTELLER FÜR MATCHES ===================== */
  useEffect(() => {
    const allManufacturers = [];

    // 1. Hersteller aus Highlight-Ständen hinzufügen (Volltextsuche)
    highlightStands.forEach((s) => {
      if (s.company) {
        allManufacturers.push({
          id: s.id,
          label: s.label,
          name: s.company.de || s.company.en,
          photo: s.photo,
          companyLegal: s.companyLegal,
          street: s.street,
          streetNumber: s.streetNumber,
          postalcode: s.postalcode,
          city: s.city,
          region: s.region,
          phone: s.phone,
          web: s.web,
          email: s.email,
          contact: s.contact,
          category: s.category, // Original für Volltextsuche behalten
          product: s.product,
          description: s.description,
        });
      }
    });

    // 2. Hersteller aus Kategorieauswahl hinzufügen
    data.stands.forEach((stand) => {
      const categoryMatch =
        selectedCategories.length > 0 &&
        selectedCategories.some((c) => {
          // Übersetzte Kategorie für Filter-Check
          const translated = t(stand.category);
          return translated.split(",").map(s => s.trim().toLowerCase())
              .includes(c.toLowerCase().trim());
        });

      if (categoryMatch && stand.company) {
        allManufacturers.push({
          id: stand.id,
          label: stand.label,
          name: stand.company.de || stand.company.en,
          photo: stand.photo,
          companyLegal: stand.companyLegal,
          street: stand.street,
          streetNumber: stand.streetNumber,
          postalcode: stand.postalcode,
          city: stand.city,
          region: stand.region,
          phone: stand.phone,
          web: stand.web,
          email: stand.email,
          contact: stand.contact,
          category: stand.category, // Original bleibt für Volltextsuche
          product: stand.product,
          description: stand.description,
        });
      }
    });

    // 3. Duplikate entfernen
    const unique = Array.from(
      new Map(allManufacturers.map((m) => [m.id ?? m.name, m])).values()
    );

    // 4. Sortieren
    const sorted = unique.sort((a, b) => {
      const parseLabel = (label) => label.split("-").map(Number);
      const la = parseLabel(a.label);
      const lb = parseLabel(b.label);
      for (let i = 0; i < Math.min(la.length, lb.length); i++) {
        if (la[i] !== lb[i]) return la[i] - lb[i];
      }
      return 0;
    });

    setManufacturersForMatches(sorted);
  }, [highlightStands, selectedCategories, t, standHasCategory]);


    /* ===================== HERSTELLER FÜR EINEN STAND ===================== */
    const getManufacturersForStand = useCallback(
      (stand) => {
        const manufacturers = [];

        // Highlight-Treffer
        const searchHit = highlightStands.find((s) => s.id === stand.id);
        if (searchHit?.company) {
          manufacturers.push({
            id: searchHit.id,
            name: searchHit.company.de || searchHit.company.en,
          });
        }

        // Kategorie-Treffer (mehrsprachig & Komma-Abgleich)
        const categoryMatch =
          selectedCategories.length > 0 &&
          selectedCategories.some((c) => standHasCategory(stand.category, c));

        if (categoryMatch && stand.company) {
          manufacturers.push({
            id: stand.id,
            name: stand.company.de || stand.company.en,
          });
        }

        // Duplikate entfernen
        return Array.from(
          new Map(manufacturers.map((m) => [m.id ?? m.name, m])).values()
        );
      },
      [highlightStands, selectedCategories, standHasCategory]
    );

  /* ===================== STAND-CENTER-BERECHNUNG ===================== */
  const getStandCenter = useCallback((stand) => {
    switch (stand.shape) {
      case "rect":
        return { x: stand.x + stand.width / 2, y: stand.y + stand.height / 2 };
      case "polygon": {
        const points = stand.points.split(" ").map((p) => p.split(",").map(Number));
        const xs = points.map((p) => p[0]);
        const ys = points.map((p) => p[1]);
        return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
      }
      case "circle":
      default:
        return { x: stand.cx, y: stand.cy };
    }
  }, []);

  /* ===================== STAND-KLICK HANDLER ===================== */
  const handleStandClick = useCallback(
    (stand) => {
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const center = getStandCenter(stand);
      setActiveStand({ ...stand, screenX: rect.left + center.x, screenY: rect.top + center.y });
    },
    [getStandCenter]
  );

  /* ===================== MATCH CHECK ===================== */
  const standHasMatch = useCallback(
    (stand) => {
      const isSearchMatch = highlightStands.some((s) => s.id === stand.id);
      const isCategoryMatch =
        selectedCategories.length > 0 &&
        selectedCategories.some((c) => standHasCategory(stand.category, c));
      return isSearchMatch || isCategoryMatch;
    },
    [highlightStands, selectedCategories, standHasCategory]
  );

  /* ===================== STAND RENDERING ===================== */
  const renderStandShape = useCallback(
    (stand) => {
      const hasMatch = standHasMatch(stand);
      const isHovered = hoveredStandId === stand.id;

      const commonProps = {
        fill: hasMatch ? (isHovered ? "#22c55e" : "#4ade80") : isHovered ? "#fb7185" : "#ff6666",
        fillOpacity: isHovered ? 0.9 : hasMatch ? 0.7 : 0.3,
        stroke: hasMatch ? "#16a34a" : "#f54848",
        strokeWidth: isHovered ? 1.5 : 0.8,
        style: { cursor: "pointer", transition: "all 0.15s ease-in-out" },
        onMouseEnter: () => setHoveredStandId(stand.id),
        onMouseLeave: () => setHoveredStandId(null),
        onClick: () => handleStandClick(stand),
      };

      const center = getStandCenter(stand);

      let shape;
      if (stand.shape === "rect") shape = <rect {...commonProps} x={stand.x} y={stand.y} width={stand.width} height={stand.height} />;
      else if (stand.shape === "polygon") shape = <polygon {...commonProps} points={stand.points} />;
      else shape = <circle {...commonProps} cx={stand.cx} cy={stand.cy} r={stand.radius} />;

      return (
        <React.Fragment key={stand.id}>
          {shape}
          {stand.label && (() => {
            const lines = t(stand.label).split("\n");
            const lineHeightEm = 1.1;
            const startDy = -((lines.length - 1) * lineHeightEm) / 2;

            return (
              <text
                x={center.x}
                y={center.y}
                textAnchor="middle"
                fontSize={stand.fontSize || 5}
                fill="#000"
                pointerEvents="none"
              >
                {lines.map((line, i) => (
                  <tspan
                    key={i}
                    x={center.x}
                    dy={i === 0 ? `${startDy}em` : `${lineHeightEm}em`}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            );
          })()}
        </React.Fragment>
      );
    },
    [standHasMatch, handleStandClick, getStandCenter, hoveredStandId, t]
  );

  /* ===================== TREFFERANZEIGE ===================== */
  const allLabels = Array.from(new Set(manufacturersForMatches.map(m => m.label))).join(" | ");
  const categoryMatches = manufacturersForMatches.filter(
    m => selectedCategories.length > 0 && selectedCategories.some(c => standHasCategory(m.category, c))
  );
  const searchMatches = manufacturersForMatches.filter(
    m => highlightStands.some(s => s.id === m.id)
  );

  /* ===================== RENDER ===================== */
  return (
    <div
      className={`
        mx-auto
        transition-all
        ${pdfMode
          ? "w-[900px] px-8"
          : "w-full px-2 sm:px-4"
        }
      `}
    >
      <div className="w-full flex-1 flex flex-col">
        <div className="flex-1 border rounded-2xl overflow-hidden p-4 bg-white">
          {/* Trefferanzeige oben */}
          {manufacturersForMatches.length > 0 && (
            <h3 className="mb-4 font-semibold text-lg">
              {lang === "de"
                ? `ITA Stand ${allLabels} (${categoryMatches.length} Kategorie-Treffer, ${searchMatches.length} Suche-Treffer)`
                : `ITA Stand ${allLabels} (${categoryMatches.length} category matches, ${searchMatches.length} search matches)`}
            </h3>
          )}

          {/* SVG Standplan */}
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            style={{ width: "100%", height: "auto" }}   // ❌ hier funktioniert "auto"
            preserveAspectRatio="xMidYMin meet"
          >
            <image href="./standplan.svg" width="100%" height="100%" />
            {data.stands.map(renderStandShape)}
          </svg>

          {/* Herstellerliste */}
          {manufacturersForMatches.length > 0 && (
            <div className="border-t p-4 mt-4 bg-white">
              {manufacturersForMatches.map((m) => {
                const isMobile = window.innerWidth < 768; // einfache Prüfung
                const imgSize = isMobile ? "300px" : "200px";
                const textSize = isMobile ? "text-lg" : "text-base";

                return (
                  <div key={m.id ?? m.name} className="mb-2 p-2 border rounded bg-gray-50" style={{ whiteSpace: "pre-line" }}>
                    {m.photo && (
                      <img
                        src={m.photo}
                        alt={m.company || "Standbild"}
                        style={{ width: "100%", maxWidth: imgSize, height: "auto", marginTop: "0.5rem", borderRadius: "0.5rem" }}
                      />
                    )}
                    <h2 className={`font-semibold mb-2 ${textSize}`}>{m.name}</h2>
                    <p><strong class="text-green-600">{m.label}</strong></p>
                    <br />
                    <div className={`${textSize}`}>
                      {<p><strong>{m.companyLegal}</strong></p>}
                      {<p>{m.street} {m.streetNumber}</p>}
                      {<p>{m.postalcode}   {m.city}</p>}
                      {<p>{m.region}</p>}
                      <br />
                      {m.contact && <h3><strong>{lang === "de" ? "Kontakt:" : "Contact:"} {t(m.contact, lang)}</strong></h3>}
                      {m.phone && <p><strong>{lang === "de" ? "Telefon:" : "Phone:"}</strong> {t(m.phone, lang)}</p>}
                      {m.web && <p><strong>{lang === "de" ? "Internet:" : "Website:"}</strong> {t(m.web, lang)}</p>}
                      {m.email && <p><strong>{lang === "de" ? "E-Mail:" : "Email:"}</strong> {t(m.email, lang)}</p>}
                      <br />
                      {m.category && <p><strong>{lang === "de" ? "Kategorie:" : "Category:"}</strong> {t(m.category, lang)}</p>}
                      {m.product && <p><strong>{lang === "de" ? "Produkte:" : "Products:"}</strong> {t(m.product, lang)}</p>}
                      <br />
                      <strong>{lang === "de" ? "Profil:" : "Profile:"}</strong>
                      {m.description && <p className="justify-content: normal;">{t(m.description, lang)}</p>}
                      <hr />
                    </div>
                  </div>
                );
              })}

            </div>
          )}

          {/* Modal für angeklickten Stand */}
          {activeStand && (
            <StandModal
              stand={activeStand}
              hasMatch={standHasMatch(activeStand)}
              manufacturers={getManufacturersForStand(activeStand)}
              onClose={() => setActiveStand(null)}
              printable={printable}
            />
          )}
        </div>
      </div>
    </div>
  );
}
